import { err, errAsync, ok, okAsync, Result, ResultAsync } from 'neverthrow'
import {
  CookieExpiredError,
  FetchTokenFailedError,
  InvalidJwtTokenError,
  InvalidMSTokenError,
  MissingAuthTokenError,
  SetExpiredCookieError,
} from '../errors'
import { OfficeRuntimeAccessToken, OfficeTokenCookie } from '../types'
import Cookies from 'js-cookie'
import { decodeJwt, JWTPayload } from 'jose'

export const safeDecodeJwt = (
  token: string
): Result<JWTPayload, InvalidJwtTokenError> => {
  try {
    const decoded = decodeJwt(token)
    return ok(decoded)
  } catch (_) {
    return err(new InvalidJwtTokenError())
  }
}

export const checkMSAuthToken = (): Result<
  string,
  MissingAuthTokenError | CookieExpiredError | InvalidJwtTokenError
> => {
  return getMSAuthTokenFromCookie().andThen(checkMSAuthTokenExpired)
}

export const getMSAuthTokenFromCookie = (): Result<
  string,
  MissingAuthTokenError
> => {
  const token = Cookies.get(OfficeTokenCookie.KEY)
  return typeof token === 'string'
    ? ok(token)
    : err(new MissingAuthTokenError())
}

export const getMSAuthTokenFromCookieAsync = (): ResultAsync<
  string,
  MissingAuthTokenError
> => {
  return getMSAuthTokenFromCookie().match(
    (token) => okAsync(token),
    (err) => errAsync(err)
  )
}

export const checkMSAuthTokenExpired = (
  token: string,
  leewaySec = 60
): Result<string, CookieExpiredError | InvalidJwtTokenError> => {
  return safeDecodeJwt(token).andThen((jwt) => {
    const now = Math.floor(Date.now() / 1000)
    const exp = typeof jwt.exp === 'number' ? jwt.exp : 0
    const nbf = typeof jwt.nbf === 'number' ? jwt.nbf : 0
    const valid = now >= nbf && now < exp - leewaySec
    return valid ? ok(token) : err(new CookieExpiredError())
  })
}

export const fetchSSOToken = () =>
  ResultAsync.fromPromise(
    Office.auth.getAccessToken(),
    () => new FetchTokenFailedError()
  )

export const setTokenToCookie = (
  token: string
): Result<string, SetExpiredCookieError> => {
  return checkMSAuthTokenExpired(token)
    .andThen(safeDecodeJwt)
    .andThen((payload) => {
      const expSec =
        typeof payload?.exp === 'number'
          ? payload.exp - 60
          : Math.floor(Date.now() / 1000) + 300
      const expires = new Date(expSec * 1000)
      Cookies.set(OfficeTokenCookie.KEY, token, {
        secure: true,
        sameSite: 'Lax',
        expires,
      })
      return ok(token)
    })
    .orElse(() => err(new SetExpiredCookieError()))
}

// Extract stable user identity details from a valid, non-expired token.
// Returns oid, name, preferredUsername. Performs:
// 1. Fetch token from cookie
// 2. Expiry validation (with leeway)
// 3. Safe decode
// 4. Shape validation via guard
export const getUserDetailsFromToken = () =>
  getMSAuthTokenFromCookie()
    .andThen(checkMSAuthTokenExpired) // ensure not expired first
    .andThen(safeDecodeJwt)
    .andThen(isJwtContainsOfficeUserInfo)

export const isJwtContainsOfficeUserInfo = (jwt: JWTPayload) => {
  if (!jwt || typeof jwt !== 'object') return err(new InvalidMSTokenError())

  const hasRequiredProps =
    typeof jwt.name === 'string' &&
    typeof jwt.preferred_username === 'string' &&
    typeof jwt.oid === 'string'

  return hasRequiredProps
    ? ok(jwt as OfficeRuntimeAccessToken)
    : err(new InvalidMSTokenError())
}

export const refetchToken = () => fetchSSOToken().andThen(setTokenToCookie)
